import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/WorkerProfile.css';
import { API_BASE_URL } from '../config';

function WorkerProfile() {
  const { userId } = useParams();
  const [ranking, setRanking] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWorkerData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch worker ranking
        const rankingResponse = await fetch(`${API_BASE_URL}/rankings/worker/${userId}`);
        if (!rankingResponse.ok) {
          throw new Error('Failed to fetch worker ranking');
        }
        const rankingData = await rankingResponse.json();
        setRanking(rankingData.ranking);

        // Fetch evaluation history
        const historyResponse = await fetch(`${API_BASE_URL}/rankings/worker/${userId}/history?limit=10`);
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setHistory(historyData.history || []);
        }
      } catch (err) {
        console.error('Error fetching worker data:', err);
        setError('Failed to load worker profile. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchWorkerData();
    }
  }, [userId]);

  const getTierColor = (tier) => {
    switch (tier) {
      case 'diamond':
        return '#60a5fa';
      case 'platinum':
        return '#a78bfa';
      case 'gold':
        return '#fbbf24';
      case 'silver':
        return '#94a3b8';
      case 'bronze':
        return '#cd7f32';
      default:
        return '#6b7280';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'diamond':
        return '💎';
      case 'platinum':
        return '🏆';
      case 'gold':
        return '🥇';
      case 'silver':
        return '🥈';
      case 'bronze':
        return '🥉';
      default:
        return '📊';
    }
  };

  if (loading) {
    return (
      <div className="worker-profile">
        <div className="loading">Loading worker profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="worker-profile">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!ranking) {
    return (
      <div className="worker-profile">
        <div className="error-message">Worker profile not found</div>
      </div>
    );
  }

  const completionRate = ranking.total_tasks_completed + ranking.total_tasks_failed > 0
    ? ((ranking.total_tasks_completed / (ranking.total_tasks_completed + ranking.total_tasks_failed)) * 100).toFixed(1)
    : 0;

  return (
    <div className="worker-profile">
      <div className="profile-header">
        <div className="rank-badge" style={{ borderColor: getTierColor(ranking.rank_tier) }}>
          <div className="tier-icon">{getTierIcon(ranking.rank_tier)}</div>
          <div className="tier-name">{ranking.rank_tier.toUpperCase()}</div>
        </div>
        <div className="worker-info">
          <h1>{ranking.users?.username || 'Worker'}</h1>
          <div className="rank-position">Global Rank: #{ranking.rank}</div>
          <div className="rank-points">{ranking.rank_points} Points</div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Tasks Completed</div>
          <div className="stat-value">{ranking.total_tasks_completed}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Quality Score</div>
          <div className="stat-value">{ranking.average_quality_score.toFixed(1)}/100</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Test Pass Rate</div>
          <div className="stat-value">{ranking.average_test_pass_rate.toFixed(1)}%</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Current Streak</div>
          <div className="stat-value">{ranking.streak_bonus} 🔥</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Completion Rate</div>
          <div className="stat-value">{completionRate}%</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Tasks Failed</div>
          <div className="stat-value">{ranking.total_tasks_failed}</div>
        </div>
      </div>

      {ranking.nextTier && (
        <div className="tier-progress">
          <div className="tier-progress-header">
            <span>Progress to {ranking.nextTier.toUpperCase()}</span>
            <span>{ranking.pointsToNextTier} points needed</span>
          </div>
          <div className="tier-progress-bar">
            <div
              className="tier-progress-fill"
              style={{
                width: `${Math.min(100, ((ranking.rank_points % 100) / 100) * 100)}%`,
                backgroundColor: getTierColor(ranking.nextTier)
              }}
            />
          </div>
        </div>
      )}

      <div className="evaluation-history">
        <h2>Recent Evaluations</h2>
        {history.length === 0 ? (
          <p className="no-history">No evaluation history yet</p>
        ) : (
          <div className="history-list">
            {history.map((evaluation) => (
              <div key={evaluation.id} className="history-item">
                <div className="history-task-info">
                  <h3>{evaluation.tasks?.title || 'Task'}</h3>
                  <div className="history-meta">
                    <span className="complexity-badge">{evaluation.tasks?.complexity}</span>
                    <span className="status-badge">{evaluation.tasks?.status}</span>
                  </div>
                </div>
                <div className="history-scores">
                  <div className="score-item">
                    <span className="score-label">Quality</span>
                    <span className="score-value">{evaluation.quality_score.toFixed(1)}/100</span>
                  </div>
                  <div className="score-item">
                    <span className="score-label">Tests Passed</span>
                    <span className="score-value">
                      {evaluation.tests_passed}/{evaluation.tests_total}
                    </span>
                  </div>
                </div>
                <div className="history-date">
                  {new Date(evaluation.evaluated_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkerProfile;
